import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './Auth.scss';
import CommonInput from '../Components/CommonInput';
import PasswordEyeToggle from '../Components/PasswordEyeToggle';
import CommonButton from '../Components/CommonButton';
import { send_otp } from '../services';
import ButtonLoader from '../Components/ButtonLoader';
import TermsService from '../Components/TermsServices';
import PrivacyPolicy from '../Components/PrivacyPolicy';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

function Signup() {
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [companyName, setCompanyName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [countryCode, setCountryCode] = useState('91'); // Default to India or empty
    const [errors, setErrors] = useState({});
    const [strength, setStrength] = useState(0);
    const [resMsg, setResMsg] = useState('');
    const [errResMsg, setErrResMsg] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showPass, setShowPass] = useState(false);
    const [showConfirmPass, setShowConfirmPass] = useState(false);
    const [termsServiceModal, setTermsServiceModal] = useState(false);
    const [privacyPolicyModal, setPrivacyPolicyModal] = useState(false);

    const location = useLocation();
    const navigate = useNavigate();

    const validate = () => {
        const newErrors = {};
        const emailRegex = /^(?![.\-])[A-Za-z0-9._%-]+(?<![.\-])@[A-Za-z0-9]+(?:-[A-Za-z0-9]+)*(?:\.[A-Za-z]{2,})+$/;
        const passwordRegex = /^(?=.{8,}$)(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()]).*$/;
        const nameRegex = /^(?!\s+$)[A-Za-z\s]+$/;;
        if (!firstName) {
            newErrors.firstName = 'First name is required.';
            setIsLoading(false);
        } else if (!nameRegex.test(firstName)) {
            newErrors.firstName = 'Name should contain only alphabets.';
            setIsLoading(false);
        }

        if (!lastName) {
            newErrors.lastName = 'Last name is required.';
            setIsLoading(false);
        } else if (!nameRegex.test(lastName)) {
            newErrors.lastName = 'Last Name should contain only alphabets.';
            setIsLoading(false);
        }

        if (!companyName) {
            newErrors.companyName = 'Company name is required.';
            setIsLoading(false);
        }

        if (!email) {
            newErrors.email = 'Email is required.';
            setIsLoading(false);
        } else if (!emailRegex.test(email)) {
            newErrors.email = 'Enter a valid email.';
            setIsLoading(false);
        }

        if (!phoneNumber || phoneNumber.length < 5) {
            newErrors.phoneNumber = 'Valid phone number is required.';
            setIsLoading(false);
        }

        if (!password) {
            newErrors.password = 'Password is required.';
            setIsLoading(false);
        } else if (!passwordRegex.test(password)) {
            newErrors.password = 'Password must be ≥8 chars and include lowercase, uppercase, number & special char.';
            setIsLoading(false);
        }

        if (!confirmPassword) {
            newErrors.confirm = 'Please confirm your password.';
            setIsLoading(false);
        } else if (confirmPassword !== password) {
            newErrors.confirm = 'Passwords do not match.';
            setIsLoading(false);
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const onChangeHandler = (e) => {
        const { name, value } = e.target;
        const setters = {
            firstName: setFirstName,
            lastName: setLastName,
            companyName: setCompanyName,
            email: setEmail,
            password: setPassword,
            confirmPassword: setConfirmPassword,
        };
        if (setters[name]) {
            setters[name](value);
            setErrResMsg('')
        }
        if (name === 'password') {
            setStrength(calculateStrength(value));
            setErrResMsg('')
        }
        setErrors('')
    };

    const calculateStrength = (password) => {
        let strength = 0;
        if (password.length >= 10) strength++;
        if (/[a-z]/.test(password)) strength++;
        if (/[A-Z]/.test(password)) strength++;
        if (/\d/.test(password)) strength++;
        if (/[!@#$%^&*()]/.test(password)) strength++;
        return Math.min(strength, 4);
    };

    const submitHandler = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        if (!validate()) return;

        try {
            const queryData = {
                emailId: email,
                otp_type: 'EMAIL_VERIFICATION',
                phone_number: phoneNumber,
                country_code: countryCode
            };
            const local_data = {
                first_name: firstName,
                last_name: lastName,
                company_name: companyName,
                emailId: email,
                password: password,
                confirm_password: confirmPassword,
                phone_number: phoneNumber,
                country_code: countryCode
            }

            const response = await send_otp(queryData);
            setIsLoading(false);
            const successMessage = response?.statusCode?.response_data?.message;
            const errorMessage = response?.errors?.message;
            const errResMsg = response?.error?.message
            if (successMessage) {
                setResMsg(successMessage);
                setIsLoading(false);
                setErrResMsg('');
                localStorage.setItem("user_data", JSON.stringify(local_data))
                localStorage.setItem('is_popup_closed', 'false');
                localStorage.setItem('company_name', local_data.company_name);
                sessionStorage.setItem('is_otp_allowed', 'signup');
                navigate('/otp-verification', { state: { from: 'signup' } });
            } else if (errResMsg) {
                setErrResMsg(errResMsg);
                setIsLoading(false);
                setResMsg('');
            } else if (errorMessage) {
                setErrResMsg(errorMessage)
                setIsLoading(false);
            }
        } catch (error) {
            setIsLoading(false);
        }
    };

    return (
        <div className="sign-up-container">
            {/* Left: Animated tagline */}
            <div className="signin-left-panel">
                <div className="signin-left-content">
                     <p className="animated-tagline-novaai">NOVA AI</p>
                    <p className="animated-tagline">Unified cyber risk intelligence platform</p>
                </div>
            </div>

            {/* Right: Sign-up Form */}
            <div className="signup-form-panel">
                <div className="sign-up-wrper">
                    <div className="logo_wrper">
                        <img src="/assets/small_logo.svg" alt="Logo" width={300} height={90} />
                    </div>
                    <div className="card">
                        <h1 className="title">Signup</h1>
                        <p className="auth_txt_color font_400 font_16">
                            Enter your company email below to signup to your account.
                        </p>
                        <form onSubmit={submitHandler} noValidate>
                            <div className="form-group">
                                <div className="wrp_name_fields">
                                    <div>
                                        <label htmlFor="firstname" className="font_500 font_16 light_black">
                                            First Name
                                        </label>
                                        <CommonInput
                                            id="firstname"
                                            name="firstName"
                                            type="text"
                                            value={firstName}
                                            onChange={onChangeHandler}
                                            placeholder="First Name"
                                            maxLength={20}
                                        />
                                        {errors.firstName && <p className="error-message">{errors.firstName}</p>}
                                    </div>
                                    <div>
                                        <label htmlFor="lastname" className="font_500 font_16 light_black">
                                            Last Name
                                        </label>
                                        <CommonInput
                                            id="lastname"
                                            name="lastName"
                                            type="text"
                                            value={lastName}
                                            onChange={onChangeHandler}
                                            placeholder="Last Name"
                                            maxLength={20}
                                        />
                                        {errors.lastName && <p className="error-message">{errors.lastName}</p>}
                                    </div>
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="companyname" className="font_500 font_16 light_black">
                                    Company Name
                                </label>
                                <CommonInput
                                    id="companyname"
                                    name="companyName"
                                    type="text"
                                    value={companyName}
                                    onChange={onChangeHandler}
                                    placeholder="Enter Company Name"
                                    maxLength={50}
                                />
                                {errors.companyName && <p className="error-message">{errors.companyName}</p>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="email" className="font_500 font_16 light_black">
                                    Work Email
                                </label>
                                <CommonInput
                                    id="email"
                                    name="email"
                                    type="email"
                                    value={email}
                                    onChange={onChangeHandler}
                                    placeholder="Enter Email"
                                    maxLength={50}
                                />
                                {errors.email && <p className="error-message">{errors.email}</p>}
                            </div>

                            <div className="form-group phone-group for_signup">
                                <label htmlFor="phone" className="font_500 font_16 light_black">
                                    Phone Number
                                </label>
                                <PhoneInput
                                    country={'in'}
                                    enableSearch={true}
                                    value={countryCode + phoneNumber}
                                    onChange={(phone, country) => {
                                        setCountryCode(country.dialCode);
                                        setPhoneNumber(phone.slice(country.dialCode.length));
                                        setErrors(prev => ({ ...prev, phoneNumber: '' }));
                                    }}
                                    inputProps={{
                                        name: 'phone',
                                        required: true,
                                        autoFocus: false
                                    }}
                                    containerClass="phone-input-container"
                                    inputClass="phone-input-field"
                                    buttonClass="phone-input-button"
                                    dropdownClass="phone-input-dropdown"
                                    searchClass="phone-input-search"
                                />
                                {errors.phoneNumber && <p className="error-message">{errors.phoneNumber}</p>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="password" className="font_500 font_16 light_black">
                                    Password
                                </label>
                                <CommonInput
                                    id="password"
                                    name="password"
                                    type={showPass ? 'text' : 'password'}
                                    value={password}
                                    onChange={onChangeHandler}
                                    placeholder="Type Strong Password"
                                    maxLength={30}
                                />
                                {errors.password && <p className="error-message">{errors.password}</p>}
                                <span className='eye_icon'>
                                    <PasswordEyeToggle visible={showPass} onClick={() => setShowPass(!showPass)} />
                                </span>
                            </div>

                            <div className="strength-indicator">
                                {Array.from({ length: 4 }).map((_, idx) => (
                                    <div
                                        key={idx}
                                        className={`bar ${idx < strength ? 'filled' : 'empty'}`}
                                    />
                                ))}
                            </div>

                            <div className="form-group">
                                <label htmlFor="confirm" className="font_500 font_16 light_black">
                                    Confirm Password
                                </label>
                                <CommonInput
                                    id="confirm"
                                    name="confirmPassword"
                                    type={showConfirmPass ? 'text' : 'password'}
                                    value={confirmPassword}
                                    onChange={onChangeHandler}
                                    placeholder="Re-enter Password"
                                    maxLength={30}
                                />
                                {errors.confirm && <p className="error-message">{errors.confirm}</p>}
                                <span className='eye_icon'>
                                    <PasswordEyeToggle visible={showConfirmPass} onClick={() => setShowConfirmPass(!showConfirmPass)} />
                                </span>
                            </div>
                            {resMsg && <p className="success-message">{resMsg}</p>}
                            {errResMsg && <p className="error-message">{errResMsg}</p>}
                            <CommonButton type="submit" text={isLoading ? <ButtonLoader /> : "Signup"} />
                        </form>

                        <p className="footer-text">
                            By signing up, you agree to the
                            <span className='cursor_pointer blue_txt' onClick={() => setTermsServiceModal(true)}> Terms of Service</span> and <span className='cursor_pointer blue_txt' onClick={() => setPrivacyPolicyModal(true)}> Data Privacy Policy.</span>

                            {' '} Already have an account?{' '}
                            <button type="button" onClick={() => navigate('/')}>
                                Login
                            </button>
                        </p>

                        {termsServiceModal &&
                            <TermsService
                                show={termsServiceModal}
                                onHide={() => setTermsServiceModal(false)}
                            />
                        }

                        {privacyPolicyModal &&
                            <PrivacyPolicy
                                show={privacyPolicyModal}
                                onHide={() => setPrivacyPolicyModal(false)}
                            />}
                    </div>
                </div>
            </div>{/* signup-form-panel */}
        </div>
    );
}

export default Signup;
